import { useEffect, useRef, useState, type FormEvent } from 'react';
import { X } from 'lucide-react';
import { ThemedDateField, ThemedSelect, ThemedTimeField } from './ThemedFormControls';
import type { Category, ChecklistItem, PlannerEvent, PlannerRecord, RecordKind, ShoppingItem, Task, Note } from '../../types';
import { createLocalId } from '../../types';

type FormState = {
  title: string;
  body: string;
  categoryId: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'in-progress' | 'completed';
  date: string;
  startTime: string;
  endTime: string;
  location: string;
  quantity: string;
  store: string;
  recurrence: 'none' | 'daily' | 'weekly' | 'monthly';
  reminder: string;
  checklist: string;
  favorite: boolean;
  pinned: boolean;
  completed: boolean;
};

const EMPTY_FORM: FormState = {
  title: '', body: '', categoryId: '', priority: 'medium', status: 'pending', date: '', startTime: '', endTime: '',
  location: '', quantity: '', store: '', recurrence: 'none', reminder: '', checklist: '', favorite: false, pinned: false, completed: false
};

const recordTitle = (record: PlannerRecord) => 'name' in record ? record.name : record.title;

const formFromRecord = (kind: RecordKind, record: PlannerRecord | null): FormState => {
  if (!record) return EMPTY_FORM;
  const common = {
    ...EMPTY_FORM,
    title: recordTitle(record),
    categoryId: record.categoryId ?? '',
    favorite: record.favorite,
    checklist: 'checklist' in record ? record.checklist.map((item) => item.text).join('\n') : ''
  };
  if (kind === 'task') {
    const task = record as Task;
    return { ...common, body: task.description, priority: task.priority, status: task.status, date: task.dueDate ?? '', startTime: task.dueTime ?? '', recurrence: task.recurrence, reminder: task.reminder ?? '' };
  }
  if (kind === 'event') {
    const event = record as PlannerEvent;
    return { ...common, body: event.description, date: event.date, startTime: event.startTime ?? '', endTime: event.endTime ?? '', location: event.location, recurrence: event.recurrence, reminder: event.reminder ?? '' };
  }
  if (kind === 'note') {
    const note = record as Note;
    return { ...common, body: note.content, pinned: note.pinned };
  }
  const item = record as ShoppingItem;
  return { ...common, body: item.notes, quantity: item.quantity, store: item.store, priority: item.priority, completed: item.completed };
};

const checklistFromText = (value: string, previous: ChecklistItem[], limit: number): ChecklistItem[] =>
  value.split('\n').map((text) => text.trim()).filter(Boolean).slice(0, limit).map((text, index) => ({
    id: previous[index]?.text === text ? previous[index].id : createLocalId('check'),
    text,
    completed: previous[index]?.text === text ? previous[index].completed : false
  }));

type Props = {
  categories: readonly Category[];
  kind: RecordKind;
  onClose: () => void;
  onSave: (kind: RecordKind, record: PlannerRecord) => Promise<void>;
  open: boolean;
  record: PlannerRecord | null;
};

export const EntryDialog = ({ categories, kind, onClose, onSave, open, record }: Props) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [entryStep, setEntryStep] = useState(1);
  const steps = kind === 'task' ? ['Basics', 'Options', 'Notes'] : kind === 'event' ? ['Basics', 'Options', 'Notes'] : kind === 'note' ? ['Note', 'Checklist'] : ['Item', 'Details'];

  useEffect(() => {
    if (open) {
      setForm(formFromRecord(kind, record));
      setError('');
      setEntryStep(1);
    }
  }, [kind, open, record]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const setField = <Key extends keyof FormState>(key: Key, value: FormState[Key]) => setForm((current) => ({ ...current, [key]: value }));

  const advanceEntryStep = () => {
    if (entryStep === 1 && kind !== 'note' && !form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (entryStep === 1 && kind === 'event' && !form.date) {
      setError('Event date is required.');
      return;
    }
    setError('');
    setEntryStep((current) => Math.min(current + 1, steps.length));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (window.matchMedia('(max-width: 767px)').matches && entryStep < steps.length) {
      advanceEntryStep();
      return;
    }
    const title = form.title.trim();
    if (kind !== 'note' && !title) {
      setEntryStep(1);
      return setError(`${kind === 'shopping' ? 'Item name' : 'Title'} is required.`);
    }
    if (kind === 'event' && !form.date) {
      setEntryStep(1);
      return setError('Event date is required.');
    }
    if (kind === 'event' && form.startTime && form.endTime && form.endTime < form.startTime) {
      setEntryStep(1);
      return setError('End time cannot be earlier than start time.');
    }
    if (kind === 'task' && form.recurrence !== 'none' && form.status === 'completed') {
      setEntryStep(2);
      return setError('Complete a recurring task from its list so only today’s occurrence is completed.');
    }

    const now = new Date().toISOString();
    const id = record?.id ?? createLocalId(kind);
    const common = { id, categoryId: form.categoryId || null, favorite: form.favorite, createdAt: record?.createdAt ?? now, updatedAt: now };
    const previousChecklist = record && 'checklist' in record ? record.checklist : [];
    let value: PlannerRecord;

    if (kind === 'task') {
      const completed = form.status === 'completed';
      value = { ...common, title, description: form.body.trim(), checklist: checklistFromText(form.checklist, previousChecklist, 100), priority: form.priority, status: form.status, dueDate: form.date || null, dueTime: form.startTime || null, recurrence: form.recurrence, reminder: form.reminder || null, completedAt: completed ? (record && 'completedAt' in record ? record.completedAt ?? now : now) : null } as Task;
    } else if (kind === 'event') {
      value = { ...common, title, description: form.body.trim(), checklist: checklistFromText(form.checklist, previousChecklist, 100), date: form.date, startTime: form.startTime || null, endTime: form.endTime || null, location: form.location.trim(), recurrence: form.recurrence, reminder: form.reminder || null } as PlannerEvent;
    } else if (kind === 'note') {
      value = { ...common, title, content: form.body, checklist: checklistFromText(form.checklist, previousChecklist, 200), pinned: form.pinned } as Note;
    } else {
      value = { ...common, name: title, quantity: form.quantity.trim(), store: form.store.trim(), priority: form.priority, notes: form.body.trim(), completed: form.completed } as ShoppingItem;
    }

    setSaving(true);
    setError('');
    try {
      await onSave(kind, value);
      onClose();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'This item could not be saved. Your previous data was kept.');
    } finally {
      setSaving(false);
    }
  };

  const label = kind === 'shopping' ? 'shopping item' : kind;

  return (
    <dialog className="entry-dialog" data-kind={kind} data-entry-step={entryStep} ref={dialogRef} onCancel={onClose}>
      <form className="entry-form" onSubmit={submit} noValidate>
        <div className="dialog-heading">
          <div><p className="eyebrow">{record ? 'Edit' : 'New'} {label}</p><h2>{record ? `Update ${label}` : `Create ${label}`}</h2></div>
          <button type="button" className="icon-button" aria-label={`Close ${label} form`} onClick={onClose}><X size={20} /></button>
        </div>

        <nav className="entry-step-nav" aria-label={`${label} form sections`}>
          {steps.map((step, index) => <button type="button" key={step} aria-current={entryStep === index + 1 ? 'step' : undefined} onClick={() => { setError(''); setEntryStep(index + 1); }}>{step}</button>)}
        </nav>

        <div className="form-grid">
          <label className="field field-wide entry-step-1"><span>{kind === 'shopping' ? 'Item name' : 'Title'}{kind !== 'note' ? ' *' : ''}</span><input autoFocus required={kind !== 'note' && kind !== 'task'} maxLength={200} value={form.title} onChange={(event) => setField('title', event.target.value)} /></label>
          {kind === 'event' && <ThemedDateField className="entry-step-1" label="Date *" required value={form.date} onChange={(value) => setField('date', value)} resetKey={open ? entryStep : 0} />}
          {kind === 'task' && <ThemedDateField className="entry-step-1" label="Due date" value={form.date} onChange={(value) => setField('date', value)} resetKey={open ? entryStep : 0} />}
          {(kind === 'task' || kind === 'event') && <ThemedTimeField className="entry-step-1" label={kind === 'task' ? 'Due time' : 'Start time'} value={form.startTime} onChange={(value) => setField('startTime', value)} resetKey={open ? entryStep : 0} />}
          {kind === 'event' && <ThemedTimeField className="entry-step-1" label="End time" value={form.endTime} onChange={(value) => setField('endTime', value)} resetKey={open ? entryStep : 0} />}
          {kind === 'event' && <label className="field field-wide field-location entry-step-1"><span>Location</span><input maxLength={300} value={form.location} onChange={(event) => setField('location', event.target.value)} /></label>}
          {kind === 'shopping' && <label className="field entry-step-1"><span>Quantity</span><input maxLength={100} value={form.quantity} onChange={(event) => setField('quantity', event.target.value)} /></label>}
          {kind === 'shopping' && <label className="field entry-step-1"><span>Store</span><input maxLength={200} value={form.store} onChange={(event) => setField('store', event.target.value)} /></label>}
          <ThemedSelect className={kind === 'event' ? 'entry-step-2' : 'entry-step-1'} label="Category" value={form.categoryId} options={[{ value: '', label: 'No category' }, ...categories.map((category) => ({ value: category.id, label: category.name }))]} onChange={(value) => setField('categoryId', value)} resetKey={open ? entryStep : 0} />
          {(kind === 'task' || kind === 'shopping') && <ThemedSelect className="entry-step-1" label="Priority" value={form.priority} options={[{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Medium' }, { value: 'high', label: 'High' }]} onChange={(value) => setField('priority', value as FormState['priority'])} resetKey={open ? entryStep : 0} />}
          {kind === 'task' && <ThemedSelect className="entry-step-2" label="Status" value={form.status} options={[{ value: 'pending', label: 'Pending' }, { value: 'in-progress', label: 'In progress' }, { value: 'completed', label: 'Completed' }]} onChange={(value) => setField('status', value as FormState['status'])} resetKey={open ? entryStep : 0} />}
          {(kind === 'task' || kind === 'event') && <ThemedSelect className="entry-step-2" label="Repeat" value={form.recurrence} options={[{ value: 'none', label: 'Does not repeat' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'monthly', label: 'Monthly' }]} onChange={(value) => setField('recurrence', value as FormState['recurrence'])} resetKey={open ? entryStep : 0} />}
          {(kind === 'task' || kind === 'event') && <ThemedTimeField className="field-reminder entry-step-2" label="Internal reminder" value={form.reminder} onChange={(value) => setField('reminder', value)} resetKey={open ? entryStep : 0} hint="Works only while the app is open." />}
          <label className={`field field-wide field-description entry-step-${kind === 'note' ? 1 : kind === 'shopping' ? 2 : 3}`}><span>{kind === 'note' ? 'Content' : kind === 'shopping' ? 'Notes' : 'Description'}</span><textarea rows={kind === 'note' ? 7 : 4} maxLength={kind === 'note' ? 50000 : kind === 'shopping' ? 2000 : 5000} value={form.body} onChange={(event) => setField('body', event.target.value)} /></label>
          {kind !== 'shopping' && <label className={`field field-wide field-checklist entry-step-${kind === 'note' ? 2 : 3}`}><span>Checklist</span><textarea rows={3} value={form.checklist} onChange={(event) => setField('checklist', event.target.value)} placeholder="One item per line" /><small>One item per line.</small></label>}
          <div className={`field-wide checkbox-row entry-step-${kind === 'task' || kind === 'event' || kind === 'note' ? 2 : 1}`}>
            <label><input type="checkbox" checked={form.favorite} onChange={(event) => setField('favorite', event.target.checked)} /> Favorite</label>
            {kind === 'note' && <label><input type="checkbox" checked={form.pinned} onChange={(event) => setField('pinned', event.target.checked)} /> Pin note</label>}
            {kind === 'shopping' && <label><input type="checkbox" checked={form.completed} onChange={(event) => setField('completed', event.target.checked)} /> Purchased</label>}
          </div>
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="dialog-actions"><button type="button" className="secondary-button" onClick={onClose}>Cancel</button><button type="submit" className="primary-button" disabled={saving}>{saving ? 'Saving...' : `Save ${label}`}</button></div>
        <div className="dialog-actions entry-mobile-actions"><button type="button" className="secondary-button" onClick={entryStep === 1 ? onClose : () => { setError(''); setEntryStep((current) => current - 1); }}>{entryStep === 1 ? 'Cancel' : 'Back'}</button><button type={entryStep === steps.length ? 'submit' : 'button'} className="primary-button" disabled={saving} onClick={entryStep === steps.length ? undefined : advanceEntryStep}>{saving ? 'Saving...' : entryStep === steps.length ? `Save ${label}` : 'Next'}</button></div>
      </form>
    </dialog>
  );
};
